import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { useTheme } from "../../middleware/ThemeContext";
import { useToast } from "../../components/toast/ToastContext";
import { SkeletonCell } from "../../components/SkeletonCell";
import NoData from "../../components/listing/NoData";
import { LoadingSpinner } from "../ui/LoadingSpinner";
import Pagination from "../../components/listing/Pagination";
import {
  useApproveOrganizationJoinRequestMutation,
  useGetOrganizationJoinRequestsQuery,
  useRejectOrganizationJoinRequestMutation,
} from "../../api/organizationApiSlice";

type PendingJoinRequestsProps = {
  organizationId?: string;
  organizationName?: string;
};

type JoinRequestRecord = {
  id: number;
  fullName: string;
  imageUrl?: string;
  statusId?: number;
};

const PENDING_STATUS_ID = 1;
const APPROVED_STATUS_ID = 14;
const REJECTED_STATUS_ID = 15;

const isPendingStatus = (statusId?: number) => {
  if (!statusId) return false;
  return statusId === PENDING_STATUS_ID;
};

const getStatusLabel = (statusId?: number) => {
  if (statusId === APPROVED_STATUS_ID) return "Approved";
  if (statusId === REJECTED_STATUS_ID) return "Rejected";
  return "Pending";
};

const getStatusVariant = (statusId?: number): "pending" | "approved" | "rejected" => {
  if (statusId === APPROVED_STATUS_ID) return "approved";
  if (statusId === REJECTED_STATUS_ID) return "rejected";
  return "pending";
};

const getRequestId = (item: any): number | undefined => {
  return item?.id ?? item?.requestId ?? item?.joinRequestId;
};

const getFullName = (item: any): string => {
  const directName =
    item?.name ||
    item?.fullName ||
    item?.requestedByName ||
    item?.requesterName ||
    item?.userName;

  if (directName && String(directName).trim()) return String(directName).trim();

  const user = item?.user || item?.contact || item?.requester || item?.member || {};
  const first = user?.firstName || item?.firstName || "";
  const last = user?.lastName || item?.lastName || "";
  const combined = `${first} ${last}`.trim();

  return combined || "Unknown User";
};

const getImageUrl = (item: any): string | undefined => {
  return (
    item?.signedUrl ||
    item?.image ||
    item?.imageUrl ||
    item?.avatar ||
    item?.user?.signedUrl ||
    item?.user?.image ||
    item?.contact?.signedUrl ||
    item?.contact?.image
  );
};

const PendingJoinRequests: React.FC<PendingJoinRequestsProps> = ({
  organizationId,
  organizationName,
}) => {
  const { theme } = useTheme();
  const showToast = useToast();
  const [processingRequestId, setProcessingRequestId] = useState<number | null>(null);
  const [processingAction, setProcessingAction] = useState<"approve" | "reject" | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useGetOrganizationJoinRequestsQuery({ organizationId, page, perPage }, {
    skip: !organizationId,
    refetchOnMountOrArgChange: true,
  });

  const [approveJoinRequest] = useApproveOrganizationJoinRequestMutation();
  const [rejectJoinRequest] = useRejectOrganizationJoinRequestMutation();

  const requests: JoinRequestRecord[] = useMemo(() => {
    const list = data?.data || data?.joinRequests || [];
    if (!Array.isArray(list)) return [];

    return list
      .map((item: any) => {
        const id = getRequestId(item);
        if (!id) return null;

        return {
          id,
          fullName: getFullName(item),
          imageUrl: getImageUrl(item),
          statusId: item?.statusId,
        };
      })
      .filter(Boolean) as JoinRequestRecord[];
  }, [data]);

  const meta = data?.meta;
  const totalItems = meta?.total ?? data?.total ?? requests.length;
  const currentPage = meta?.page ?? page;
  const currentLimit = meta?.limit ?? perPage;
  const totalPages = meta?.totalPages ?? Math.ceil(totalItems / currentLimit);
  const requestCount = meta?.total ?? requests.length;

  const toggleRequest = async (requestId: number, action: "approve" | "reject") => {
    if (!organizationId) return;

    try {
      setProcessingRequestId(requestId);
      setProcessingAction(action);

      if (action === "approve") {
        await approveJoinRequest({ organizationId, requestId }).unwrap();
        showToast("Request approved", "The join request has been approved.", "success");
      } else {
        await rejectJoinRequest({ organizationId, requestId }).unwrap();
        showToast("Request rejected", "The join request has been rejected.", "success");
      }

      refetch();
    } catch (error: any) {
      showToast(
        "Error",
        error?.data?.message || "Failed to update join request. Please try again.",
        "error"
      );
    } finally {
      setProcessingRequestId(null);
      setProcessingAction(null);
    }
  };

  const paginationElement = (
    !isLoading && !isFetching && requests.length > 0 && (
      <PaginationWrapper theme={theme}>
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          perPage={currentLimit}
          totalPages={totalPages}
          onPageChange={(newPage) => setPage(newPage)}
          onLimitChange={(newLimit) => {
            setPerPage(newLimit);
            setPage(1);
          }}
        />
      </PaginationWrapper>
    )
  );

  return (
    <Wrapper>
      <Header>
        <Title theme={theme}>
          Join Requests
          <Count theme={theme}>({requestCount})</Count>
        </Title>
      </Header>
      <ListWrap>
        {isLoading || isFetching ? (
          Array(5)
            .fill(null)
            .map((_, index) => (
              <SkeletonCell
                key={index}
                style={{ width: "100%", height: "58px", borderRadius: "10px" }}
              />
            ))
        ) : !requests.length ? (
          <NoData
            title="No Join Requests"
            subtitle="There are no organization join requests right now."
            containerHeight={420}
          />
        ) : (
          requests.map((request) => {
            const isCurrent = processingRequestId === request.id;
            const isPending = isPendingStatus(request.statusId);

            return (
              <RequestRow key={request.id} theme={theme}>
                <PersonWrap>
                  {request.imageUrl ? (
                    <AvatarImage src={request.imageUrl} alt={request.fullName} />
                  ) : (
                    <AvatarFallback theme={theme}>{request.fullName.charAt(0)}</AvatarFallback>
                  )}
                  <TextWrap theme={theme}>
                    <strong>{request.fullName}</strong> wants to join your organization
                    {organizationName ? ` (${organizationName})` : ""}.
                    {isPending ? " You can Accept or reject this request." : " Request is already reviewed."}
                  </TextWrap>
                </PersonWrap>

                <Actions>
                  {isPending ? (
                    <>
                      <ActionButton
                        type="button"
                        disabled={isCurrent}
                        variant="accept"
                        theme={theme}
                        onClick={() => toggleRequest(request.id, "approve")}
                      >
                        {isCurrent && processingAction === "approve" ? (
                          <LoadingSpinner size="14px" color={theme?.CorporateTheme?.colors?.primary} />
                        ) : (
                          "Accept"
                        )}
                      </ActionButton>

                      <ActionButton
                        type="button"
                        disabled={isCurrent}
                        variant="reject"
                        theme={theme}
                        onClick={() => toggleRequest(request.id, "reject")}
                      >
                        {isCurrent && processingAction === "reject" ? (
                          <LoadingSpinner size="14px" color={theme?.CorporateTheme?.colors?.danger || "#dc3545"} />
                        ) : (
                          "Reject"
                        )}
                      </ActionButton>
                    </>
                  ) : (
                    <StatusBadge theme={theme} variant={getStatusVariant(request.statusId)}>
                      {getStatusLabel(request.statusId)}
                    </StatusBadge>
                  )}
                </Actions>
              </RequestRow>
            );
          })
        )}
      </ListWrap>
      {paginationElement}
    </Wrapper>
  );
};

export default PendingJoinRequests;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2px;
`;

const Title = styled.h3<{ theme: any }>`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.CorporateTheme.colors.headerColor};
`;

const Count = styled.span<{ theme: any }>`
  margin-left: 4px;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.CorporateTheme.colors.count};
`;

const ListWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
`;

const RequestRow = styled.div<{ theme: any }>`
  width: 100%;
  min-height: 58px;
  border-radius: 10px;
  background: ${({ theme }) => theme.CorporateTheme.colors.background};
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 14px;
`;

const PersonWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const AvatarImage = styled.img`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
`;

const AvatarFallback = styled.div<{ theme: any }>`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${({ theme }) => theme.CorporateTheme.colors.bannerBg};
  color: ${({ theme }) => theme.CorporateTheme.colors.headerColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const TextWrap = styled.div<{ theme: any }>`
  color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
  font-size: 14px;
  line-height: 1.4;

  strong {
    color: ${({ theme }) => theme.CorporateTheme.colors.headerColor};
    font-weight: 600;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button<{ theme: any; variant: "accept" | "reject" }>`
  border: none;
  min-width: 64px;
  height: 30px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  color: ${({ variant, theme }) =>
    variant === "accept"
      ? theme?.CorporateTheme?.colors?.primary
      : theme?.CorporateTheme?.colors?.danger || "#dc3545"};

  background: ${({ variant }) =>
    variant === "accept" ? "rgba(23, 162, 73, 0.12)" : "rgba(220, 53, 69, 0.12)"};

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const StatusBadge = styled.span<{ theme: any; variant: "pending" | "approved" | "rejected" }>`
  min-width: 84px;
  height: 30px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  color: ${({ variant, theme }) => {
    if (variant === "approved") return theme?.CorporateTheme?.colors?.primary;
    if (variant === "rejected") return theme?.CorporateTheme?.colors?.danger || "#dc3545";
    return theme?.CorporateTheme?.colors?.headerColor;
  }};

  background: ${({ variant }) => {
    if (variant === "approved") return "rgba(23, 162, 73, 0.12)";
    if (variant === "rejected") return "rgba(220, 53, 69, 0.12)";
    return "rgba(108, 117, 125, 0.12)";
  }};
`;

const PaginationWrapper = styled.div<{ theme: any }>`
  position: sticky;
  bottom: 0;
  background: ${({ theme }) => theme.CorporateTheme.colors.background};
  border-top: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  text-align: right;
  z-index: 5;
  padding: 8px 14px;
  margin-top: 8px;
`;
